import { Badge, cn, SearchInput, Spinner } from '@platform/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { useMessages } from '../i18n';
import { type SearchResult } from '../lib/api';
import { useGlobalSearch } from '../lib/search';
import { useCanOpen } from '../lib/session';

/**
 * The search box in the top bar.
 *
 * One box for products, orders and customers, because the person typing does
 * not know which of the three they want. Asking them to pick a category first
 * is asking them to answer the question they came to ask.
 *
 * The API answers products to a session that may read the product list, and
 * orders and customers to one that may read orders — the pages its results
 * lead to. So the box asks those routes, and a session that may open neither
 * is offered nothing to type into.
 */
export function GlobalSearch({ className }: { className?: string }) {
    const t = useMessages();
    const navigate = useNavigate();
    const canOpen = useCanOpen();

    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [debounced, setDebounced] = useState('');
    const container = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(query), 200);
        return () => window.clearTimeout(timer);
    }, [query]);

    // Closes when the click lands anywhere else. Without it the panel stays over
    // the page after a result is chosen, which reads as the app having hung.
    useEffect(() => {
        function onPointerDown(event: PointerEvent) {
            if (!container.current?.contains(event.target as Node)) setOpen(false);
        }
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, []);

    const results = useGlobalSearch(debounced);

    function go(result: SearchResult) {
        setOpen(false);
        setQuery('');
        switch (result.kind) {
            case 'product':
                navigate({
                    to: '/urunler/$productId',
                    params: { productId: result.id },
                });
                break;
            case 'order':
                navigate({
                    to: '/siparisler/$orderId',
                    params: { orderId: result.id },
                });
                break;
            default:
                // The customer's own page holds what the seller was looking for —
                // their orders, contact details and notes — and the API answers a
                // customer result only to a session that may open that page.
                navigate({
                    to: '/musteriler/$customerId',
                    params: { customerId: result.id },
                });
        }
    }

    const items = results.data?.results ?? [];

    if (!canOpen('/urunler') && !canOpen('/siparisler')) return null;

    return (
        <div ref={container} className={cn('relative', className)}>
            <SearchInput
                size="sm"
                placeholder={t.common.search.placeholder}
                aria-label={t.common.search.placeholder}
                value={query}
                onFocus={() => setOpen(true)}
                onChange={(event) => {
                    setQuery(event.target.value);
                    setOpen(true);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') setOpen(false);
                    // Enter on the first result, because that is what a keyboard user
                    // expects and reaching for the mouse to confirm a search is a step
                    // nobody wants.
                    const first = items[0];
                    if (event.key === 'Enter' && first) go(first);
                }}
            />

            {open && query.trim().length > 0 && (
                <div
                    role="listbox"
                    aria-label={t.common.search.results}
                    className="absolute inset-x-0 top-full z-40 mt-2 overflow-hidden rounded-card border border-border bg-popover text-popover-foreground shadow-overlay"
                >
                    {query.trim().length < 2 ? (
                        <p className="p-4 text-13 text-muted-foreground">{t.common.search.hint}</p>
                    ) : results.isPending ? (
                        <p className="p-4 text-13 text-muted-foreground">
                            <Spinner size="sm" label={t.common.loading} />
                        </p>
                    ) : items.length === 0 ? (
                        <p className="p-4 text-13 text-muted-foreground">{t.common.search.empty}</p>
                    ) : (
                        <ul className="max-h-96 overflow-y-auto">
                            {items.map((result) => (
                                <li key={`${result.kind}-${result.id}`}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected="false"
                                        onClick={() => go(result)}
                                        className="flex w-full items-center gap-3 px-4 py-3 text-start outline-none hover:bg-accent focus-visible:bg-accent"
                                    >
                                        <Badge tone={result.kind === 'order' ? 'accent' : 'neutral'}>
                                            {t.common.search.kind[result.kind as keyof typeof t.common.search.kind] ?? result.kind}
                                        </Badge>
                                        {/* A long product title must not push the subtitle off the row. */}
                                        <span className="min-w-0 truncate font-medium">{result.title}</span>
                                        {result.subtitle && <span className="shrink-0 text-muted-foreground">{result.subtitle}</span>}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
